import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSettings, useSystem } from "@/lib/hooks";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, ShieldCheck, Cloud } from "lucide-react";

export default function SettingsPage() {
  const { data: settings, mutate } = useSettings();
  const { data: sys } = useSystem();
  const [base, setBase] = useState("");
  const [caddy, setCaddy] = useState("");
  const [ws, setWs] = useState("");
  const [cfTunnel, setCfTunnel] = useState(false);
  const [cur, setCur] = useState("");
  const [n1, setN1] = useState("");
  const [n2, setN2] = useState("");
  const [saving, setSaving] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setBase(settings.base_domain || "");
      setCaddy(settings.caddy_config_path || "");
      setWs(settings.workspace_path || "");
      setCfTunnel(!!settings.cloudflare_tunnel);
    }
  }, [settings]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", {
        base_domain: base, caddy_config_path: caddy, workspace_path: ws,
        cloudflare_tunnel: cfTunnel,
      });
      toast.success("Settings saved");
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "save failed");
    } finally {
      setSaving(false);
    }
  };

  const changePw = async () => {
    if (n1.length < 6) { toast.error("New password must be at least 6 chars"); return; }
    if (n1 !== n2) { toast.error("Passwords do not match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: n1 });
      toast.success("Password updated");
      setCur(""); setN1(""); setN2("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "change password failed");
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Base domain, workspace, admin credentials.</p>
      </header>

      <Card className="p-6 border-border">
        <h2 className="text-lg font-semibold mb-1">Platform</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Applied when generating Dockerfile + docker-compose + Caddy configs.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="base">Base domain</Label>
            <Input id="base" data-testid="settings-base-domain-input"
                   value={base} onChange={(e) => setBase(e.target.value)}
                   className="font-mono" />
            <div className="text-[11px] text-muted-foreground">Sites will be at <code className="font-mono">&lt;subdomain&gt;.{base || "emhost.local"}</code></div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="caddy">Caddy config path</Label>
            <Input id="caddy" data-testid="settings-caddy-path-input"
                   value={caddy} onChange={(e) => setCaddy(e.target.value)}
                   className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ws">Workspace path</Label>
            <Input id="ws" value={ws} onChange={(e) => setWs(e.target.value)} className="font-mono" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end">
          <Button data-testid="settings-save-button" onClick={save} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-1">
          <Cloud className="h-5 w-5" />
          <h2 className="text-lg font-semibold">External HTTPS terminator</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Enable when your traffic is fronted by a Cloudflare Tunnel (or similar) that
          terminates TLS upstream. Caddy will then only listen on plain HTTP and skip
          Let&apos;s Encrypt entirely (which cannot succeed when the domain resolves to
          the tunnel, not the VPS).
        </p>
        <div className="flex items-center justify-between p-3 rounded-md border border-border"
             style={{ background: "hsl(220 20% 10%)" }}>
          <div>
            <div className="font-medium">Cloudflare Tunnel mode</div>
            <div className="text-xs text-muted-foreground">
              When enabled, new Caddy snippets use <code className="font-mono">http://&lt;domain&gt;</code>{" "}
              (HTTP-only, no auto HTTPS). Existing sites can be updated by clicking{" "}
              <i>Regenerate configs</i> in each site&apos;s Config tab, then Deploy.
            </div>
          </div>
          <Switch
            checked={cfTunnel}
            onCheckedChange={setCfTunnel}
            data-testid="settings-cloudflare-tunnel-switch"
          />
        </div>
        <div className="mt-4 flex items-center justify-end">
          <Button data-testid="settings-save-tunnel-button" onClick={save} disabled={saving} className="gap-2">
            <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Change admin password</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="cur">Current password</Label>
            <Input id="cur" type="password" value={cur} onChange={(e) => setCur(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="n1">New password</Label>
            <Input id="n1" type="password" value={n1} onChange={(e) => setN1(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="n2">Confirm new password</Label>
            <Input id="n2" type="password" value={n2} onChange={(e) => setN2(e.target.value)} />
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end">
          <Button data-testid="settings-change-password-button" onClick={changePw} disabled={pwSaving}>
            {pwSaving ? "Updating…" : "Update password"}
          </Button>
        </div>
      </Card>

      <Card className="p-6 border-border">
        <h2 className="text-lg font-semibold">System</h2>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <div><span className="text-muted-foreground">Mode:</span> {sys?.mode}</div>
          <div><span className="text-muted-foreground">Docker:</span> {sys?.docker_available ? "available" : "unavailable"}</div>
          <div><span className="text-muted-foreground">Disk free:</span> {Math.round((sys?.disk_free_mb || 0) / 1024)} GB</div>
        </div>
      </Card>
    </div>
  );
}
